import type { SurveyPage, SkipLogic, SurveyQuestion } from '../../fetchSurvey/types/survey';
import type { LogicContext, SkipTarget } from '../types';
import { evaluateCondition } from './evaluateCondition';
import { findPageIndexForQuestion } from '../navigation/findPageIndexForQuestion';
import { logSkipLogicMatch, logSkipLogicRejected } from '../logger/logicLogger';

export type SkipEvaluationOptions = {
  /** When true, skip match/reject logs are suppressed (path simulation, progress calc). */
  silent?: boolean;
};

export function evaluateSkipLogics(
  skipLogics: SkipLogic[] | undefined,
  context: LogicContext,
  pages: SurveyPage[],
  fromPageIndex: number,
  questionId: string,
  options?: SkipEvaluationOptions
): SkipTarget | null {
  if (!skipLogics?.length) return null;

  const silent = options?.silent ?? false;

  for (const rule of skipLogics) {
    if (!evaluateCondition(rule.condition, context)) continue;

    if (rule.skipTo.entityType === 'END_OF_SURVEY') {
      if (!silent) {
        logSkipLogicMatch(questionId, rule.id, 'END_OF_SURVEY', fromPageIndex);
      }
      return { type: 'END_OF_SURVEY', skipLogicId: rule.id };
    }

    if (rule.skipTo.entityType === 'QUESTION') {
      const targetPageIndex = findPageIndexForQuestion(pages, rule.skipTo.id);
      if (targetPageIndex === -1) {
        if (!silent) {
          logSkipLogicRejected(questionId, rule.id, 'target_not_found');
        }
        continue;
      }
      if (targetPageIndex < fromPageIndex) {
        if (!silent) {
          logSkipLogicRejected(questionId, rule.id, 'backward_target');
        }
        continue;
      }

      if (!silent) {
        logSkipLogicMatch(questionId, rule.id, 'QUESTION', fromPageIndex);
      }
      return {
        type: 'QUESTION',
        questionId: rule.skipTo.id,
        pageIndex: targetPageIndex,
        skipLogicId: rule.id,
      };
    }
  }

  return null;
}

export function resolveSkipTarget(
  question: SurveyQuestion,
  context: LogicContext,
  pages: SurveyPage[],
  fromPageIndex: number,
  options?: SkipEvaluationOptions
): SkipTarget | null {
  return evaluateSkipLogics(
    question.skipLogics,
    context,
    pages,
    fromPageIndex,
    question.id,
    options
  );
}
